import {
  Controller,
  FormProvider,
  useFieldArray,
  useForm,
} from "react-hook-form";
import { ConditionSetting } from "./conditionSetting";
import { CouponCode } from "./couponCode";
import {
  ApplyToLevel,
  ApplyToProductType,
  DiscountType,
  ICreateCouponForm,
  IPromotionEventIdsItem,
  UserType,
} from "interfaces";
import { HttpError, useCreate, useOne, useParse } from "@refinedev/core";
import { CreateCouponSchema } from "./schema";
import { zodResolver } from "@hookform/resolvers/zod";
import { Link, useNavigate } from "react-router-dom";
import Button from "components/button";
import { AdditionalSetup } from "./additionalSetup";
import { useState } from "react";
import { FullScreenLoading } from "components/fullScreenLoading";
import toast from "react-hot-toast";
import { endpointService } from "components/shared/services/endpointService";
import { CouponAlreadyExistsModal } from "../../modal";
import { get, isEmpty } from "lodash";
import moment from "moment";
import { canAccessByType } from "utills/helpers";

const INITIAL_DUPLICATE_CODE_MODAL_CONFIG: any = {
  open: false,
  data: null,
};

export const CreateCoupon = () => {
  const { mutate, isLoading: isLoading } = useCreate();
  const navigate = useNavigate();
  const parse = useParse();
  const { params } = parse();

  const { data: eventData, isFetching: isFetchingEvents } = useOne<
    IPromotionEventIdsItem[],
    HttpError
  >({
    resource: endpointService.getPromotionEndPoint("getPromotionEventDropdown"),
    id: "",
    meta: {
      resource: "data",
    },
    successNotification: (data, values, resource) => {
      const uniqueCurrencies = (data?.data ?? [])
        .map((obj) => obj.currency)
        .filter(
          (currency, index, array) =>
            typeof currency === "string" && array.indexOf(currency) === index
        );
      setEventCurrencies(uniqueCurrencies);
      return {
        message: `Successfully fetched data from ${resource}.`,
        description: "Success with no errors",
        type: "success",
      };
    },
  });

  const methods = useForm<ICreateCouponForm, HttpError, ICreateCouponForm>({
    defaultValues: {
      apply_to_product_type: ApplyToProductType.INCLUDE,
      is_active: true,
      discount_type_display: DiscountType.PERCENT,
      event_ids: [],
      apply_from: moment().format("YYYY-MM-DD HH:mm:ss"),
      apply_to: moment().endOf("day").format("YYYY-MM-DD HH:mm:ss"),
      apply_to_level: ApplyToLevel.PER_ITEM,
      note: "",
      apply_product_ids: [],
      special_discount: false,
      coupon_code: null,
      customer_code: null,
      ...(canAccessByType(UserType.ADMIN) && {
        max_times_used: 999999,
      }),
    },
    mode: "onChange",
    resolver: (values, context, options) => {
      const createResolver = zodResolver(
        CreateCouponSchema(
          isShowPaymentMethod,
          isShowUsagePerAccount,
          isShowDailyActiveTime,
          eventCurrencies
        )
      );
      return createResolver(values, context, options);
    },
  });

  const [isShowPaymentMethod, setIsShowPaymentMethod] = useState(false);
  const [isShowUsagePerAccount, setIsShowUsagePerAccount] = useState(false);
  const [isShowDailyActiveTime, setIsShowDailyActiveTime] = useState(false);

  const [eventCurrencies, setEventCurrencies] = useState<string[]>([]);
  const [duplicateCodeModalConfig, setDuplicateCodeModalConfig] = useState(
    INITIAL_DUPLICATE_CODE_MODAL_CONFIG
  );

  const codeFieldArrController = useFieldArray({
    control: methods.control,
    name: "coupons.code_info_list",
  });

  const _onCloseDuplicateCodeModal = () => {
    setDuplicateCodeModalConfig({
      ...INITIAL_DUPLICATE_CODE_MODAL_CONFIG,
      data: duplicateCodeModalConfig.data,
    });
  };

  const onSubmit = (data: ICreateCouponForm) => {
    const submitValues = { ...data };
    mutate(
      {
        resource: endpointService.getPromotionEndPoint("createPromotionCoupon"),
        meta: {
          ignoreResourceWrapper: true,
        },
        values: {
          ...submitValues,
        },
      },
      {
        onSuccess: () => {
          toast.success("Create successfully");
          if (params?.redirect === "condition") {
            navigate("/promotion/condition");
          } else {
            navigate("/promotion/coupon");
          }
        },
        onError: (error) => {
          console.log(error);
          const conflictCodes = get(error, "response.data.error.conflicts", []);
          if (!isEmpty(conflictCodes)) {
            setDuplicateCodeModalConfig({
              open: true,
              data: conflictCodes,
            });
          }
        },
      }
    );
  };

  return (
    <FormProvider {...methods}>
      <form onSubmit={methods.handleSubmit(onSubmit)} className="w-full h-full">
        <div className="flex flex-col gap-y-2 sm:gap-y-4">
          <ConditionSetting
            codeFieldArrController={codeFieldArrController}
            eventCurrencies={eventCurrencies}
            setEventCurrencies={setEventCurrencies}
            isFetchingEvents={isFetchingEvents}
            eventIds={eventData?.data ?? []}
          />
          <CouponCode codeFieldArrController={codeFieldArrController} />
          {canAccessByType(UserType.ADMIN) && (
            <AdditionalSetup
              isShowPaymentMethod={isShowPaymentMethod}
              isShowUsagePerAccount={isShowUsagePerAccount}
              isShowDailyActiveTime={isShowDailyActiveTime}
              setIsShowPaymentMethod={setIsShowPaymentMethod}
              setIsShowUsagePerAccount={setIsShowUsagePerAccount}
              setIsShowDailyActiveTime={setIsShowDailyActiveTime}
            />
          )}
          <div className="w-full px-4 py-4 sm:px-6 bg-white sticky bottom-0 z-[2] mt-auto">
            <div className="flex items-center justify-end gap-x-4">
              {/* <Link to={`/mkt/sub-category${id ? `/${id}` : ""}`}>
                <Button className="w-20 bg-white text-brand-500 border border-solid border-brand-500 hover:bg-gray-50 active:bg-gray-100">
                  Cancel
                </Button>
              </Link> */}
              <Button className="w-20" type="submit">
                Save
              </Button>
            </div>
          </div>
          {isLoading ? <FullScreenLoading /> : <></>}
        </div>
      </form>
      <CouponAlreadyExistsModal
        open={duplicateCodeModalConfig.open}
        couponData={duplicateCodeModalConfig.data}
        events={eventData?.data ?? []}
        onClose={_onCloseDuplicateCodeModal}
      />
    </FormProvider>
  );
};
